import { Form } from 'antd';
import { PackingTypeSale, PackingTypeSaleMap } from 'enums/packingTypeSale';
import { usePurchaseInfoDisplay } from 'hooks/usePurchaseInfoDisplay';
import { useShippingFee } from 'hooks/useShippingFee';
import Packing from 'model/Packing';
import ProductOffer from 'model/ProductOffer';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as BuyIntensionSelectors from 'store/state/buyIntention/buyIntenteionSelectors';
import { actions as buyIntentionActions } from 'store/state/buyIntention/buyIntentionSlice';
import { getShowPinnedFilter } from 'store/state/offerFilters/offerFiltersSelectors';
import { sessionSelectors } from 'store/state/selectors';
import { calculateMinBySaleType } from 'utils/util';
import { validatorUtils } from 'utils/validator';

interface Params {
  product: ProductOffer;
  packingSelected: Packing;
  selectedTypeSale: PackingTypeSale;
  onChangePackingTypeSale: (packingTypeSale: PackingTypeSale) => void;
  onClickFastBuy: (
    quantityPacking: number,
    selectedTypeSale: PackingTypeSale,
  ) => void;
  onInvalidQuantity?: () => void;
}

export function useBuyIntentionOfferPurchase({
  product,
  packingSelected,
  selectedTypeSale,
  onChangePackingTypeSale,
  onClickFastBuy,
  onInvalidQuantity,
}: Params) {
  const dispatch = useDispatch();

  const showPinnedFilter = useSelector(getShowPinnedFilter);
  const submitOffers = useSelector(BuyIntensionSelectors.getSubmitOffers);
  const idsSelected = useSelector(BuyIntensionSelectors.getIdsProductSelected);
  const idsAccepted = useSelector(BuyIntensionSelectors.getIdsProductAccepted);
  const { customers } = useSelector(sessionSelectors.getRoot);
  const selectedCustomerId = useSelector(
    sessionSelectors.getSelectedCustomerId,
  );

  const validationComposition =
    product.isCompositionEnabled && product.productCategoryId === 1;

  const validationQuantity = validationComposition
    ? calculateMinBySaleType(
        selectedTypeSale,
        packingSelected,
        product.minimunQuantityCutProductComposition,
        validationComposition,
      )
    : packingSelected?.minimumQuantity;

  const formValidation = {
    quantityPacking: [
      {
        required: true,
        message: 'A quantidade é obrigatória.',
      },
      {
        validator: (_: any, value: string) =>
          !value || Number(value) <= getMaxAvailable.max
            ? Promise.resolve()
            : Promise.reject(
                new Error(
                  `A quantidade máxima é ${getMaxAvailable.max} ${textInputTypeSale}`,
                ),
              ),
      },
      {
        validator: (_: any, value: string) =>
          !value ||
          (validationComposition
            ? Number(value)
            : Number(value) * getMaxAvailable.min) >= validationQuantity
            ? Promise.resolve()
            : Promise.reject(new Error(getErrorMinMessage())),
      },
      {
        validator: (_: any, value: number) =>
          validatorUtils.validMultiple(
            value,
            packingSelected?.multiple ?? 0,
            selectedTypeSale,
            getMaxAvailableLabel,
            getMultipleCalc(),
            textInputTypeSale,
          ),
      },
    ],
  };

  const [form] = Form.useForm();

  const [onlyBuy, setOnlyBuy] = useState(false);
  const [quantityPacking, setQuantityPacking] = useState(0);
  const [selectedCustomer, _setSelectedCustomer] = useState(
    customers?.find((customer) => customer.id === selectedCustomerId),
  );

  const { shippingFee, hasShippingFee } = useShippingFee(product);

  const [showTrolleyModal, setShowTrolleyModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<'addToCart' | 'fastBuy'>(
    'addToCart',
  );
  const purchaseInfo = usePurchaseInfoDisplay(
    selectedTypeSale,
    packingSelected,
    quantityPacking,
    shippingFee,
  );

  const textInputTypeSale = useMemo(
    () => `${PackingTypeSaleMap[selectedTypeSale]}(s)`,
    [selectedTypeSale],
  );

  const getMaxAvailableLabel = useMemo(() => {
    const prop = `maxAvailable${selectedTypeSale}sLabel` as keyof Packing;
    return packingSelected[prop] as string;
  }, [packingSelected, selectedTypeSale]);

  const getMaxAvailable = useMemo(() => {
    if (selectedTypeSale === PackingTypeSale.Packing) {
      return {
        label: packingSelected?.maxAvailablePackingsLabel || '',
        max: packingSelected?.maxAvailablePackingsQuantity || 0,
        min: packingSelected?.unitsPerPackage,
      };
    }
    if (selectedTypeSale === PackingTypeSale.Layer) {
      return {
        label: packingSelected?.maxAvailableLayersLabel || '',
        max: packingSelected?.maxAvailableLayersQuantity || 0,
        min:
          packingSelected?.packagingByLayer * packingSelected?.unitsPerPackage,
      };
    }
    return {
      label: packingSelected?.maxAvailableTrolleysLabel || '',
      max: packingSelected?.maxAvailableTrolleysQuantity || 0,
      min:
        packingSelected?.packagingByLayer *
        packingSelected?.layersByTrolley *
        packingSelected?.unitsPerPackage,
    };
  }, [packingSelected, selectedTypeSale]);

  const totalPrice = useMemo(() => {
    if (selectedTypeSale === PackingTypeSale.Packing) {
      return purchaseInfo.totalUnits * product.packagingPrice;
    }
    if (selectedTypeSale === PackingTypeSale.Layer) {
      return purchaseInfo.totalUnits * product.layerPrice;
    }
    return purchaseInfo.totalUnits * product.trolleyPrice;
  }, [
    selectedTypeSale,
    purchaseInfo.totalUnits,
    product.packagingPrice,
    product.layerPrice,
    product.trolleyPrice,
  ]);

  const getMin = (
    selectedTypeSale: PackingTypeSale,
    packingSelected: Packing,
    calcField: 'multiple' | 'minimumQuantity',
    prefrerQuantity?: number,
    preferQauntityActive?: boolean,
  ) => {
    const minimum =
      preferQauntityActive && prefrerQuantity
        ? prefrerQuantity * +packingSelected?.maxAvailablePackingsLabel
        : packingSelected[calcField];
    if (selectedTypeSale === PackingTypeSale.Packing) {
      const calc = minimum / +packingSelected?.maxAvailablePackingsLabel;
      return calc < 1 ? 1 : calc;
    }
    const values = (
      selectedTypeSale === PackingTypeSale.Layer
        ? packingSelected.maxAvailableLayersLabel
        : packingSelected.maxAvailableTrolleysLabel
    )
      .split('x')
      .map((x) => +x)
      .reduce((a, b) => +a * +b);
    const calc = minimum / values;
    return calc < 1 ? 1 : Math.ceil(calc);
  };

  const handleTypeSale = (packingType: PackingTypeSale) => {
    onChangePackingTypeSale(packingType);
  };

  const getErrorMinMessage = useCallback(
    () =>
      `Quantidade mínima: ${getMin(
        selectedTypeSale,
        packingSelected,
        'minimumQuantity',
        product.minimunQuantityCutProductComposition,
        validationComposition,
      )} ${textInputTypeSale}`,
    [
      selectedTypeSale,
      packingSelected,
      textInputTypeSale,
      product.minimunQuantityCutProductComposition,
      validationComposition,
    ],
  );

  const getMultipleCalc = useCallback(
    () => getMin(selectedTypeSale, packingSelected, 'multiple'),
    [selectedTypeSale, packingSelected],
  );

  const fastBuyProducts = () => {
    if (idsAccepted.indexOf(product.id) !== -1 && onlyBuy) {
      onClickFastBuy(quantityPacking, selectedTypeSale);
      setOnlyBuy(false);
      dispatch(buyIntentionActions.setShowModalOffers(false));
      dispatch(buyIntentionActions.setIsLoading(true));
    }
  };

  const handleTrolleyConfirm = useCallback(() => {
    setShowTrolleyModal(false);
    dispatch(buyIntentionActions.setIdsProductsAccepted(product.id));
  }, [dispatch, product.id]);

  const onSubmit = useCallback(
    (data: { quantityPacking: string }) => {
      if (purchaseInfo.exceedsTrolley) {
        setPendingAction('addToCart');
        setShowTrolleyModal(true);
        return;
      }
      dispatch(buyIntentionActions.setIdsProductsAccepted(product.id));
    },
    [dispatch, product.id, purchaseInfo.exceedsTrolley],
  );

  const handleClickFastBuy = useCallback(() => {
    form.submit();
  }, [form]);

  const calculateSubTotal = (value: number) => {
    if (selectedTypeSale === 'Packing') {
      const subPrice =
        value *
        packingSelected.unitsPerPackage *
        (packingSelected.additionalCost
          ? product.packagingPrice + product.packingCost + (shippingFee ?? 0)
          : product.packagingPrice + (shippingFee ?? 0));

      dispatch(
        buyIntentionActions.setSubTotalList({
          id: product.id,
          value: subPrice,
        }),
      );
    }
    if (selectedTypeSale === 'Layer') {
      const subPrice =
        value *
        packingSelected.packagingByLayer *
        packingSelected.unitsPerPackage *
        (packingSelected.additionalCost
          ? product.layerPrice + product.packingCost + (shippingFee ?? 0)
          : product.layerPrice + (shippingFee ?? 0));

      dispatch(
        buyIntentionActions.setSubTotalList({
          id: product.id,
          value: subPrice,
        }),
      );
    }
    if (selectedTypeSale === 'Trolley') {
      const subPrice =
        value *
        packingSelected.layersByTrolley *
        packingSelected.packagingByLayer *
        packingSelected.unitsPerPackage *
        (packingSelected.additionalCost
          ? product.trolleyPrice + product.packingCost + (shippingFee ?? 0)
          : product.trolleyPrice + (shippingFee ?? 0));

      dispatch(
        buyIntentionActions.setSubTotalList({
          id: product.id,
          value: subPrice,
        }),
      );
    }
  };

  useEffect(() => {
    calculateSubTotal(quantityPacking);
  }, [selectedTypeSale, packingSelected]);

  useEffect(() => {
    if (submitOffers) {
      if (idsSelected.indexOf(product.id) !== -1) {
        setOnlyBuy(true);
        form.submit();
      }
      dispatch(buyIntentionActions.setSubmitOffers(false));
    }
  }, [submitOffers]);

  useEffect(() => {
    if (idsSelected.length === idsAccepted.length && idsAccepted.length !== 0) {
      fastBuyProducts();
    }
    dispatch(buyIntentionActions.setSubmitOffers(false));
  }, [idsAccepted]);

  const changeQuantity = (value: number) => {
    if (value >= 0) {
      setQuantityPacking(value);
      form.setFieldsValue({ quantityPacking: value });
      calculateSubTotal(value);
    }
    if (value > 0) {
      dispatch(buyIntentionActions.setIdsProductsSelected(product.id));
    } else {
      dispatch(buyIntentionActions.setDelIds(product.id));
    }
  };

  return {
    form,
    formValidation,
    showPinnedFilter,
    quantityPacking,
    changeQuantity,
    purchaseInfo,
    getMaxAvailable,
    textInputTypeSale,
    totalPrice,
    shippingFee,
    hasShippingFee,
    showTrolleyModal,
    setShowTrolleyModal,
    handleTrolleyConfirm,
    handleTypeSale,
    onSubmit,
    onSubmitFailed: () => onInvalidQuantity?.(),
    handleClickFastBuy,
  };
}

export type BuyIntentionOfferPurchase = ReturnType<
  typeof useBuyIntentionOfferPurchase
>;
